import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiCheck } from 'react-icons/fi';

// Step configuration
const STEPS = [
  { id: 'country', number: 1 },
  { id: 'date', number: 2 },
  { id: 'duration', number: 3 }
];

// Timeline container
const TimelineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 320px;
  margin: ${({ theme }) => theme.spacing.md} auto 0;
  padding: 0;
  
  @media (max-width: 480px) {
    max-width: 280px;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Step circle
const StepCircle = styled(motion.div)`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  z-index: 3;
  position: relative;
  flex-shrink: 0;
  
  /* Completed state */
  ${({ $completed, theme }) => $completed && `
    background: ${theme.colors.accent};
    color: ${theme.colors.background.main};
    box-shadow: 0 0 15px rgba(16, 185, 129, 0.2);
  `}
  
  /* Current state */
  ${({ $current, theme }) => $current && `
    background: ${theme.colors.primary};
    color: white;
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  `}
  
  /* Future state */
  ${({ $future, theme }) => $future && `
    background: transparent;
    color: rgba(248, 250, 252, 0.3);
    border: 1.5px solid rgba(148, 163, 184, 0.2);
  `}
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

// Connecting line
const ConnectingLine = styled.div`
  flex: 1;
  height: 2px;
  background: rgba(148, 163, 184, 0.1);
  margin: 0 8px;
  border-radius: 1px;
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

const ProgressLine = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 1px;
`;

// Main component
export const IntegratedTimeline = ({
  currentStep,
  completedSteps = []
}) => {
  const getStepStatus = (stepId) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'future';
  };

  return (
    <TimelineContainer>
      {STEPS.map((step, index) => {
        const status = getStepStatus(step.id);
        const isCompleted = status === 'completed';
        const isCurrent = status === 'current';
        const isFuture = status === 'future';
        const isLast = index === STEPS.length - 1;

        return (
          <React.Fragment key={step.id}>
            <StepWrapper>
              <StepCircle
                $completed={isCompleted}
                $current={isCurrent}
                $future={isFuture}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: isCurrent ? 1.15 : 1, opacity: 1 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.05,
                  type: "spring",
                  stiffness: 200
                }}
              >
                {isCompleted ? (
                  <motion.div
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <FiCheck size={16} />
                  </motion.div>
                ) : (
                  step.number
                )}
              </StepCircle>
            </StepWrapper>

            {!isLast && (
              <ConnectingLine>
                <ProgressLine
                  initial={{ width: "0%" }}
                  animate={{ width: isCompleted ? "100%" : "0%" }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut",
                    delay: 0.1
                  }}
                />
              </ConnectingLine>
            )}
          </React.Fragment>
        );
      })}
    </TimelineContainer>
  );
};

export default IntegratedTimeline;
